<script setup lang="ts">
import { useModalSlot } from 'vue-final-modal'
import { PR022, PR039 } from '~/enums/shop-goods-enums'
import { useLoginInfoStore } from '~/stores/member/login-info-store'
import { popupHandler } from '~/composables/popup/popup-handler'
import { useCounselQnaService } from '~/services/cs/counsel-qna-service'
import ImageModal from '~/components/mypage/counsel/ImageModal.vue'
import type { qna } from '~/types/goods/goods-review'
import { getImgBaseUrl, isNotEmptyArray } from '~/utils/shop-common'

const { loginDrvtModalOpen } = popupHandler()
const loginInfoStore = useLoginInfoStore()
const { getUserInfo: userInfo } = storeToRefs(loginInfoStore)

const { $filter, $toast } = useNuxtApp()

const { qnAData } = defineProps<{
  qnAData: qna
}>()

const emit = defineEmits<{
  (e: 'reloadQna'): void
}>()

const isSecretQnA = computed(() => qnAData.scrtScrpYn === 'Y')
const isWaitingAnswer = computed(() => qnAData.procStatCd === PR039.PROC_YET || qnAData.procStatCd === PR039.CUST_CNTR_MVOT)
const isFinishedAnswer = computed(() => qnAData.procStatCd === PR039.PROC_FIN)

const isLoginMemberQnA = computed(() =>
  userInfo.value?.mbrNo === qnAData.mbrNo && isWaitingAnswer.value,
)

// 상품 문의 삭제 팝업
async function onClickDeleteHandler() {
  if (!loginDrvtModalOpen(true)) return

  const { open, close } = useLocaDialog({
    content: '상품문의를 삭제할까요?',
    btnList: [
      { text: '아니요', type: 'cancel', callback: () => close() },
      {
        text: '네',
        type: 'primary',
        callback: async () => {
          try {
            const { request } = useCounselQnaService().deleteGoodsQnaCounsel
            const res = await request(qnAData.questNo)
            if (res.code === '0000') {
              $toast.add({ text: '상품문의를 삭제했어요', notification: { bottom: 104 } })
              emit('reloadQna')
            }
          }
          catch (e) {
            console.error(e)
            $toast.add({ text: '상품문의 삭제 실패했어요', notification: { bottom: 104 } })
          }
          await close()
        },
      },
    ],
  })
  await open()
}

function openImagePopUp(contentsList: any) {
  const photoList = contentsList.map((content) => {
    return {
      ...content,
      src: getImgBaseUrl(content.contFilePathNm),
    }
  })
  const { close: fullClose, open: fullOpen } = useLocaModal({
    isHistoryPushState: true,
    attrs: {},
    slots: {
      default: useModalSlot({
        component: ImageModal,
        attrs: {
          photoList,
          onClose() {
            fullClose()
          },
        },
      }),
    },
    openType: { mobile: 'full', pc: 'full' },
  })
  fullOpen()
}
function decodeCont(ansCont) {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = ansCont
  return textarea.value
}

// css
const lineCss = ref(true)
function hideLineCss() {
  lineCss.value = false
}

function showLineCss() {
  lineCss.value = true
}

// [DEV] 240913 PJ85-20902
const onClickQuestion = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.classList.contains('line-clamp-2')) {
    target.classList.remove('line-clamp-2')
  }
  else {
    target.classList.add('line-clamp-2')
  }
}
</script>

<template>
  <div>
    <template v-if="isWaitingAnswer || isSecretQnA">
      <div class="p-4">
        <div class="flex items-center pb-1.5">
          <!-- [DEV] 240815 - 클래스명 수정 -->
          <span
            class="shop-c-label font-normal mr-2"
            :class="isFinishedAnswer ? 'text-on-view-06 bg-secondary' : 'text-on-view-02 bg-warm-gray-100'"
          >
            {{ isFinishedAnswer ? '답변완료' : '답변대기' }}
          </span>
          <span class="flex items-center gap-x-1.5 text-16 font-medium mr-auto">
            {{ qnAData.questTypNm }}
            <template v-if="isSecretQnA">
              <i-normal-lock class="w-4 h-4" />
            </template>
          </span>
        </div>
        <button
          type="button"
          class="block w-full text-left text-16 break-all"
          :class="!isLoginMemberQnA && isSecretQnA ? 'cursor-auto' : ''"
          @click="onClickQuestion"
        >
          <span
            class="text-16 line-clamp-2"
            :class="!isLoginMemberQnA && isSecretQnA ? 'text-on-view-03' : 'text-on-view-02'"
          >
            {{ !isLoginMemberQnA && isSecretQnA ? '비공개 문의글 입니다.' : qnAData.questCont }}
          </span>
        </button>
        <div
          v-if="isNotEmptyArray(qnAData.contents)"
          class="grid grid-cols-4 gap-x-1 mt-2.5"
        >
          <button
            v-for="(content, index) in qnAData.contents"
            :key="index"
            type="button"
            @click="openImagePopUp(qnAData.contents)"
          >
            <ShopCommonImage
              :src="getImgBaseUrl(content.contFilePathNm, '160', '160')"
            />
          </button>
        </div>
        <div class="flex items-center justify-between mt-4">
          <div class="flex items-center gap-x-2">
            <span class="text-14 text-on-view-03">{{ qnAData.mbrNm }}</span>
            <span class="divider-horizontal-02 h-3 self-auto" />
            <span class="text-14 text-on-view-03">{{ $filter.yyyyMMdd_dot(qnAData.questDtm) }}</span>
          </div>
          <button
            v-if="isLoginMemberQnA"
            type="button"
            class="btn btn-link btn-xs !text-btn-link-default text-14 font-medium px-0"
            @click="onClickDeleteHandler"
          >
            삭제
          </button>
        </div>
      </div>
      <div class="divider-01" />
    </template>
    <ShopCollapseDefault
      v-else-if="isFinishedAnswer"
      :id="`qna-${qnAData.questNo}`"
      class="pt-4"
    >
      <template #customTrigger="{ triggerA11yProps, onClickTrigger }">
        <div class="pb-4 px-4">
          <div class="flex items-center pb-1.5">
            <!-- [DEV] 240815 - 클래스명 수정 -->
            <span
              class="shop-c-label font-normal mr-2"
              :class="isFinishedAnswer ? 'text-on-view-06 bg-secondary' : 'text-on-view-02 bg-warm-gray-100'"
            >
              {{ isFinishedAnswer ? '답변완료' : '답변대기' }}
            </span>
            <button
              type="button"
              v-bind="triggerA11yProps"
              class="flex items-center justify-between grow"
              @click="onClickTrigger"
            >
              <span class="flex items-center gap-x-1.5 text-16 font-medium mr-auto">
                {{ qnAData.questTypNm }}
                <template v-if="isSecretQnA">
                  <i-normal-lock class="w-4 h-4" />
                </template>
              </span>
              <!-- [S] - [DEV] 240912 - 상품상세 접근성 대응 -->
              <span v-if="triggerA11yProps['aria-expanded']">
                <i-normal-arrow-up-onview-01
                  class="w-6 h-6"
                  @click="showLineCss"
                />
                <span class="sr-only">Q&A 답변 접기</span>
              </span>
              <span v-else>
                <i-normal-arrow-down-onview-01
                  class="w-6 h-6"
                  @click="hideLineCss"
                />
                <span class="sr-only">Q&A 답변 펼치기</span>
              </span>
              <!-- [E] - [DEV] 240912 - 상품상세 접근성 대응 -->
            </button>
          </div>
          <p
            class="text-16 text-on-view-02 break-all"
            :class="triggerA11yProps['aria-expanded'] ? '' : 'line-clamp-2'"
          >
            {{ qnAData.questCont }}
          </p>
          <div
            v-if="isNotEmptyArray(qnAData.contents)"
            class="grid grid-cols-4 gap-x-1 mt-2.5"
          >
            <button
              v-for="(content, index) in qnAData.contents"
              :key="index"
              type="button"
              @click="openImagePopUp(qnAData.contents)"
            >
              <ShopCommonImage
                :src="getImgBaseUrl(content.contFilePathNm, '160', '160')"
                :alt="content.alt || ''"
              />
            </button>
          </div>
          <div class="flex items-center gap-x-2 mt-4">
            <span class="text-14 text-on-view-03">{{ qnAData.mbrNm }}</span>
            <span class="divider-horizontal-02 h-3 self-auto" />
            <span class="text-14 text-on-view-03">{{ $filter.yyyyMMdd_dot(qnAData.questDtm) }}</span>
          </div>
        </div>
        <div
          v-if="lineCss"
          class="divider-01"
        />
      </template>
      <template #content>
        <template
          v-for="(data, index) in qnAData.answerList"
          :key="data.id"
        >
          <div
            v-if="data.ansDispStatCd === PR022.DISP_Y"
            class="p-4 bg-warm-gray-30"
          >
            <p
              v-dompurify-html="decodeCont(data.ansCont)"
              class="mb-4 text-16 text-on-view-02"
            />
            <span class="text-14 text-on-view-03">{{ $filter.yyyyMMdd_dot(data.sysRegDtm) }}</span>
          </div>
          <div
            v-if="!(index + 1 === qnAData.answerList.length)"
            class="
            divider-02"
          />
        </template>
      </template>
    </ShopCollapseDefault>
  </div>
</template>

<style scoped>

</style>
